/**
 * Firebase Configuration
 *
 * This module initializes and exports Firebase services for the NightOwl NEO Portal.
 * Firebase is used for analytics tracking to understand user behavior and improve the product.
 *
 * Configuration is environment-specific (dev, qa, prod) and credentials should be
 * provided via environment variables.
 */

import { Analytics, getAnalytics, isSupported, setUserProperties } from 'firebase/analytics';
import { FirebaseApp, getApps, initializeApp } from 'firebase/app';

import env from 'configs/env';

import { getFirebaseConfig } from './firebaseConfig';

let firebaseApp: FirebaseApp | null = null;
let firebaseAnalytics: Analytics | null = null;

/**
 * Initialize Firebase app and analytics.
 * This should be called once at application startup.
 */
export async function initializeFirebase(): Promise<void> {
  // Prevent re-initialization
  if (getApps().length > 0) {
    firebaseApp = getApps()[0];
    return;
  }

  const config = getFirebaseConfig();

  // Validate required configuration
  if (!config.apiKey || !config.projectId) {
    console.warn('Firebase configuration is incomplete. Analytics will be disabled.');
    return;
  }

  try {
    firebaseApp = initializeApp(config);

    // Check if analytics is supported in the current environment
    const analyticsSupported = await isSupported();

    if (analyticsSupported && typeof window !== 'undefined') {
      firebaseAnalytics = getAnalytics(firebaseApp);

      // Set environment as a user property for all analytics events
      // This allows filtering/comparing dev/qa/prod in GA4 dashboards
      setUserProperties(firebaseAnalytics, {
        environment: env.environment || 'unknown',
      });

      console.log(`Firebase Analytics initialized for environment: ${env.environment}`);
    } else {
      console.warn('Firebase Analytics is not supported in this environment.');
    }
  } catch (error) {
    console.error('Failed to initialize Firebase:', error);
  }
}

/**
 * Get the Firebase app instance.
 */
export function getFirebaseApp(): FirebaseApp | null {
  return firebaseApp;
}

/**
 * Get the Firebase Analytics instance.
 */
export function getFirebaseAnalytics(): Analytics | null {
  return firebaseAnalytics;
}

export { getFirebaseConfig };
